import React, { useState, useMemo } from "react";

import { Button, CircularProgress } from "@material-ui/core";

import { ButtonWithSpinnerProps } from "./props";
import { ButtonPropsBorderSide } from "../../../models/enums/button-props-border-side";
import { ButtonPropsFill } from "../../../models/enums/button-props-fill";
import { ButtonPropsColor } from "../../../models/enums/button-props-color";
import { ButtonPropsBorderRadius } from "../../../models/enums/button-props-border-radius";
import { useStyles } from "./css";
import { Color } from "../../../constants/colors";


export default function ButtonWithSpinner(props: ButtonWithSpinnerProps) {

  const {
    width = "fit-content",
    height = 32,
    margin,
    borderRadius = ButtonPropsBorderRadius.Small,
    borderSide = ButtonPropsBorderSide.All,
    fill = ButtonPropsFill.Filled,
    backgroundOrBorderColor = ButtonPropsColor.Main,
    innerText,
    textColor = fill === ButtonPropsFill.Filled
      ? backgroundOrBorderColor === ButtonPropsColor.Gray1
        ? ButtonPropsColor.Gray4
        : ButtonPropsColor.Gray1
      : backgroundOrBorderColor,
    innerIcon,
    hoverColor,
    onClick,
    disabled = false,
    isProcessing
  } = props;

  const [localIsProcessing, setLocalIsProcessing] = useState(false);

  const background = fill === ButtonPropsFill.Filled ? backgroundOrBorderColor : "transparent";
  const borderColor = fill === ButtonPropsFill.Outlined ? backgroundOrBorderColor : "transparent";

  const customStyles = useMemo(() => ({
    minWidth: width,
    height: height,
    margin: margin,
    borderRadius: getBorderRadiusStyles(),
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: borderColor,
    backgroundColor: background,
    color: textColor,
    "&:hover": getHoverStyles(),
    "&:disabled": {
      color: textColor,
      opacity: 0.4
    },
  }), [width, height, margin, borderRadius, borderSide, backgroundOrBorderColor, fill, textColor, hoverColor]);

  const classes = useStyles(customStyles);

  function getHoverStyles() {
    if (fill === ButtonPropsFill.Filled && !hoverColor) {
      return {
        backgroundColor: background,
        color: textColor
      };
    } else if (hoverColor) {
      return {
        backgroundColor: hoverColor,
        color: [ButtonPropsColor.Gray1, ButtonPropsColor.Clilac].includes(hoverColor)
          ? (backgroundOrBorderColor === ButtonPropsColor.None ? textColor : backgroundOrBorderColor)
          : Color.gray1,
      };
    }
    return {};
  }

  function getBorderRadiusStyles() {
    switch (borderSide) {
      case ButtonPropsBorderSide.All:
        return borderRadius;
      case ButtonPropsBorderSide.Left:
        return `${borderRadius}px 0 0 ${borderRadius}px`;
      case ButtonPropsBorderSide.Right:
        return `0 ${borderRadius}px ${borderRadius}px 0`;
    }
  };

  async function handleButtonClick(event?: React.MouseEvent<HTMLButtonElement>) {
    setLocalIsProcessing(true);
    const result = event ? onClick(event) : onClick();
    if (result && typeof result.then === 'function') {
      await result;
    }
    setLocalIsProcessing(false);
  }

  return (
    <Button
      className={classes.button}
      onClick={handleButtonClick}
      disabled={disabled || isProcessing || localIsProcessing}
    >
      <div className={classes.buttonItems}>
        {(isProcessing ?? localIsProcessing)
          ? <CircularProgress
            style={{ width: 14, height: 14, color: textColor }}
          />
          : innerIcon ? innerIcon : null
        }
        {innerText}
      </div>
    </Button>
  );
};
